import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TargetingAdminStore } from '../../stores';
import { IAGroupList } from '../../../groupList/GroupList';
import { IASpinner } from '../../../spinner/Spinner';
import EditPanel from './panels/EditPanel';
import { IAButton } from '../../../button/Button';
import { IAPopup } from '../../../popup/Popup';
import { IACommandBar } from '../../../commandBar/CommandBar';
import { TargetingService } from '../../../../services/TargetingService';
import { Helper } from '../../../../Helper';
import * as styles from './UserGroup.css';
import { LocalizationService } from '../../../../services/LocalizationService';
import { IAPivot } from '../../../pivot/Pivot';
import { ITreeViewItem } from '../../../../interfaces/ITreeViewItem';
import GroupHeaderEditPanel from './panels/GroupHeaderEditPanel';
import ADGroupPanel from './panels/ADGroupPanel';
import { ITargetingGroup } from '../../../../interfaces/ITargetingGroup';
import { IAIcon } from '../../../icon/Icon';
import { IATooltip } from '../../../tooltip/Tooltip';
import { TargetingV2Service } from '../../../../services/TargetingV2Service';
import StructureViewer from '../../../structureViewer/StructureViewer';
import { IStructureElement } from '../../../structureViewer/IStructureElement';

export interface IUserGroupProps {
  targetingStore?: TargetingAdminStore;
  reloadGroups: () => void;
}

export interface IUserGroupState {
  selectedTab: string;
  expandAddMenu?: boolean;
  saving: boolean;
  loadingGroupStructure: boolean;
  fromScratch?: boolean;
}

@inject('targetingStore')
@observer
export default class UserGroup extends React.Component<IUserGroupProps, IUserGroupState> {
  private readonly HEADER_ICON = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Text/SVG/ic_fluent_text_32_regular.svg";
  private readonly GROUP_ICON = "https://intraactivestorage.blob.core.windows.net/cdn/icons/People/SVG/ic_fluent_people_48_regular.svg";

  private menuRef: React.RefObject<HTMLDivElement>
  private readonly localizationService: LocalizationService = new LocalizationService();
  private deletePopupId;
  private cancelPopupId;

  constructor(props, state) {
    super(props, state);
    this.deletePopupId = Helper.getRandomStringKey();
    this.cancelPopupId = Helper.getRandomStringKey();
    this.state = {
      selectedTab: "ADGroups",
      saving: false,
      loadingGroupStructure: true
    };
    this.menuRef = React.createRef();
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    this.getStructuredGroups();
  }

  public componentDidMount() {
    document.addEventListener('mousedown', (e) => this.handleClick(e))
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', (e) => this.handleClick(e));
  }

  private getStructuredGroups(): void {
    TargetingV2Service.getTargetingStructure(this.props.targetingStore.environment, this.props.targetingStore.tenant).then((structuredGroups: ITreeViewItem[]) => {
      this.props.targetingStore.structuredGroupsBeforeChanges = structuredGroups;
      this.props.targetingStore.structuredGroups = structuredGroups;
      this.setState({ loadingGroupStructure: false, fromScratch: structuredGroups.length === 0 });
    });
  }

  private handleClick(event: Event) {
    if (this.menuRef && this.menuRef.current && !this.menuRef.current.contains(event.target as Node)) {
      if (this.state.expandAddMenu) {
        this.setState({ expandAddMenu: false })
      }
    }
  }

  private getChildren(items: ITreeViewItem[]): ITreeViewItem[] {
    let groupChildren: ITreeViewItem[] = [];
    if (items.length > 0) {
      items.forEach((childGroup: ITreeViewItem) => {
        let groupChild: ITreeViewItem;
        this.props.targetingStore.groupsList.forEach((dataItem) => {
          if (childGroup.id === dataItem.id) {
            groupChild = {
              ...dataItem,
              children: childGroup.children,
            };
          }
        });
        if (!groupChild) {
          groupChild = childGroup;
        }
        groupChildren.push(
          {
            title: groupChild.title,
            id: groupChild.id,
            children: groupChild.children?.length > 0 ? this.getChildren(groupChild.children) : []
          }
        );
      });
    } else {
      groupChildren = this.props.targetingStore.groupsList;
    }
    return groupChildren;
  }

  private getUnselectedADGroups(): any[] {
    let unselectedADGroups = [];
    this.props.targetingStore.groupsList?.forEach(((group: ITargetingGroup) => {
      const hasAlreadyBeenAdded = this.isGroupIdPartOfGroup(group.id, this.props.targetingStore.structuredGroups, false);
      group.disabled = hasAlreadyBeenAdded;
      unselectedADGroups.push(group)
    }));
    return unselectedADGroups;
  }

  private isGroupIdPartOfGroup(id: string, groups: ITreeViewItem[], isGroupIdPartOfGroup: boolean): boolean {
    let value = isGroupIdPartOfGroup;
    if (groups.length > 0) {
      groups.forEach((group: ITreeViewItem) => {
        if (group?.children?.length > 0) {
          value = this.isGroupIdPartOfGroup(id, group?.children, value)
          if (value) {
            value = true;
          }
        }
        if (group.id === id) {
          value = true;
        }
      })
    }
    return value;
  }

  private setHighlightForItemInGroups(itemToEdit: ITreeViewItem, groups: ITreeViewItem[]): ITreeViewItem[] {
    if (groups.length > 0) {
      groups.forEach((group: ITreeViewItem) => {
        if (group?.children?.length > 0) {
          group.children = this.setHighlightForItemInGroups(itemToEdit, group?.children)
        }
        if (group.id === itemToEdit.id) {
          group.highlighted = itemToEdit.highlighted == undefined ? true : !itemToEdit.highlighted;
        }
      })
    }
    return groups;
  }

  private deleteItemInGroups(itemToDelete: ITreeViewItem, groups: ITreeViewItem[]): ITreeViewItem[] {
    if (groups.length > 0) {
      groups.forEach((group: ITreeViewItem, index) => {
        if (group?.children?.length > 0) {
          group.children = this.deleteItemInGroups(itemToDelete, group?.children)
        }
        if (group === itemToDelete) {
          groups.splice(index, 1);
        }
      })
    }
    return groups;
  }

  private moveItem(itemToMove: ITreeViewItem, groups: ITreeViewItem[], direction: "up" | "down"): ITreeViewItem[] {
    if (groups.length > 0) {
      let oldIndex;
      let newIndex;
      groups.forEach((group: ITreeViewItem, index) => {
        if (group?.children?.length > 0) {
          group.children = this.moveItem(itemToMove, group?.children, direction)
        }
        if (group === itemToMove) {
          oldIndex = index;
          newIndex = direction === "up" ? index - 1 : index + 1;
        }
      });
      if (oldIndex != undefined) {
        groups.splice(oldIndex, 1);
        groups.splice(newIndex, 0, itemToMove);
      }
    }
    return groups;
  }

  private moveItemOut(itemToMoveOut: ITreeViewItem, groups: ITreeViewItem[], parentGroup?: ITreeViewItem[]): ITreeViewItem[] {
    if (groups.length > 0) {
      groups.forEach((group: ITreeViewItem, index) => {
        if (group?.children?.length > 0) {
          group.children = this.moveItemOut(itemToMoveOut, group?.children, groups);
        }
        if (group === itemToMoveOut) {
          groups.splice(index, 1);
          parentGroup.push(itemToMoveOut);
        }
      })
    }
    return groups;
  }

  private updateGroupHeaderTitle(newTitle: string, groups: ITreeViewItem[]): ITreeViewItem[] {
    if (groups.length > 0) {
      groups.forEach((group: ITreeViewItem, index) => {
        if (group?.children?.length > 0) {
          group.children = this.updateGroupHeaderTitle(newTitle, group?.children)
        }
        if (group === this.props.targetingStore.groupHeaderToEdit) {
          groups[index].title = newTitle;
        }
      })
    }
    return groups;
  }

  private onSave(): void {
    this.setState({ saving: true });
    if (this.state.fromScratch) {
      TargetingV2Service.createTargetingStructure(this.props.targetingStore.environment, this.props.targetingStore.tenant, this.props.targetingStore.structuredGroups).then(() => {
        this.setState({ saving: false });
        this.props.targetingStore.structuredGroupsBeforeChanges = JSON.parse(JSON.stringify(this.props.targetingStore.structuredGroups));
      });
    } else {
      TargetingV2Service.updateTargetingStructure(this.props.targetingStore.environment, this.props.targetingStore.tenant, this.props.targetingStore.structuredGroups).then(() => {
        this.setState({ saving: false });
        this.props.targetingStore.structuredGroupsBeforeChanges = JSON.parse(JSON.stringify(this.props.targetingStore.structuredGroups));
      });
    }
  }

  private getExampleStructure(): IStructureElement[] {
    return [
      {
        title: this.localizationService.strings.TargetingAdmin_GroupHeader,
        type: "header",
        highlighted: false,
        iconUrl: this.HEADER_ICON,
        id: "",
        originalElement: undefined,
        children: [
          {
            title: this.localizationService.strings.TargetingAdmin_ADGroup,
            type: "element",
            highlighted: false,
            iconUrl: this.GROUP_ICON,
            id: "",
            originalElement: undefined,
            children: []
          },
          {
            title: this.localizationService.strings.TargetingAdmin_ADGroup,
            type: "element",
            highlighted: false,
            iconUrl: this.GROUP_ICON,
            id: "",
            originalElement: undefined,
            children: []
          },
          {
            title: this.localizationService.strings.TargetingAdmin_GroupHeader,
            type: "header",
            highlighted: false,
            iconUrl: this.HEADER_ICON,
            id: "",
            originalElement: undefined,
            children: [
              {
                title: this.localizationService.strings.TargetingAdmin_ADGroup,
                type: "element",
                highlighted: false,
                iconUrl: this.GROUP_ICON,
                id: "",
                originalElement: undefined,
                children: []
              },
              {
                title: this.localizationService.strings.TargetingAdmin_ADGroup,
                type: "element",
                highlighted: false,
                iconUrl: this.GROUP_ICON,
                id: "",
                originalElement: undefined,
                children: []
              },
            ]
          }
        ]
      },
      {
        title: this.localizationService.strings.TargetingAdmin_GroupHeader,
        type: "header",
        highlighted: false,
        iconUrl: this.HEADER_ICON,
        id: "",
        originalElement: undefined,
        children: [
          {
            title: this.localizationService.strings.TargetingAdmin_ADGroup,
            type: "element",
            highlighted: false,
            iconUrl: this.GROUP_ICON,
            id: "",
            originalElement: undefined,
            children: []
          }, {
            title: this.localizationService.strings.TargetingAdmin_ADGroup,
            type: "element",
            highlighted: false,
            iconUrl: this.GROUP_ICON,
            id: "",
            originalElement: undefined,
            children: []
          }
        ]
      }
    ];
  }

  private convertTreeItemToStructureElement(item: ITreeViewItem): IStructureElement {
    const isADGroup = !!item.id;
    return {
      id: item.id,
      title: isADGroup ? this.props.targetingStore.groupsList.find(group => group.id === item.id).title : item.title,
      type: isADGroup ? "element" : "header",
      iconUrl: isADGroup
        ? this.GROUP_ICON
        : this.HEADER_ICON,
      highlighted: item.highlighted,
      children: item.children ? item.children.filter((groupItem) => groupItem.title || this.props.targetingStore.groupsList?.find((existingGroupItem) => existingGroupItem.id === groupItem.id)).map(child => this.convertTreeItemToStructureElement(child)) : undefined,
      originalElement: item
    }
  }

  public render(): JSX.Element {
    let allInUse = true;
    this.getUnselectedADGroups().forEach((group: ITargetingGroup) => {
      if (!group.disabled) {
        allInUse = false;
      }
    });
    const structureNotSetUp = this.props.targetingStore.structuredGroups?.length === 0;
    let structure: IStructureElement[];
    if (structureNotSetUp) {
      structure = this.getExampleStructure();
    } else {
      structure = this.props.targetingStore.structuredGroups
        ?.filter((groupItem) => groupItem.title || this.props.targetingStore.groupsList?.find((existingGroupItem) => existingGroupItem.id === groupItem.id))
        ?.map(item => this.convertTreeItemToStructureElement(item));
    }
    const saveButton = (
      <IAButton
        label={this.localizationService.strings.Admin_Save}
        iconColor={"#ffffff"}
        textColor={"#ffffff"}
        borderRadius={5}
        disbaled={!allInUse || JSON.stringify(this.props.targetingStore.structuredGroupsBeforeChanges) === JSON.stringify(this.props.targetingStore.structuredGroups)}
        showSpinner={this.state.saving}
        buttonColor={this.props.targetingStore.highlightColor}
        onClick={() => {
          this.onSave();
        }}
        style={{
          marginLeft: 10,
        }}
      />
    );
    return (
      <div>
        <IAPivot
          asTabs
          autoTabWidth
          selectedTabBackgrondColor={this.props.targetingStore.highlightColor}
          tabBackgrondColor="#eaeaea"
          highlightColor="#ffffff"
          height={30}
          showIcon
          items={[
            {
              key: "ADGroups",
              text: this.localizationService.strings.TargetingAdmin_ADGroups,
              tooltip: this.localizationService.strings.TargetingAdmin_UserGroup_TabText,
              icon: "https://intraactivestorage.blob.core.windows.net/cdn/icons/People/SVG/ic_fluent_people_48_regular.svg"
            },
            {
              key: "GroupStructure",
              text: this.localizationService.strings.TargetingAdmin_GroupStructure,
              tooltip: this.localizationService.strings.TargetingAdmin_GroupStructureInfo,
              icon: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Organization/SVG/ic_fluent_organization_48_regular.svg"
            }
          ]}
          style={{
            marginTop: 10,
            marginBottom: 30,
            float: "left",
            borderRadius: 30,
            overflow: "hidden",
            width: "fit-content",
            clear: "both"
          }}
          selectedKey={this.state.selectedTab}
          select={(selectedTab: string) => this.setState({ selectedTab })}
        />
        {/* GroupStructure */}
        {this.state.selectedTab === "GroupStructure" &&
          <div style={{
            float: 'left',
            clear: "both",
            width: "100%",
            paddingBottom: 50,
            boxSizing: "border-box"
          }}>
            <div
              style={{ float: 'left', position: 'relative', marginBottom: 30, clear: "both" }}
              ref={this.menuRef}
            >
              <IAButton
                label={this.localizationService.strings.Admin_Add}
                icon="Add"
                iconColor={"#ffffff"}
                textColor={"#ffffff"}
                borderRadius={5}
                disbaled={this.state.loadingGroupStructure}
                buttonColor={this.props.targetingStore.highlightColor}
                onClick={() => {
                  this.setState({ expandAddMenu: !this.state.expandAddMenu });
                }}
              />
              {this.state.expandAddMenu &&
                <ul className={styles.IA_cardBarItems}>
                  <div
                    className={styles.IA_commandBarItem}
                    onClick={() => {
                      this.props.targetingStore.addNewTopLevelADGroup = true;
                    }}
                  >
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/People%20Community%20Add/SVG/ic_fluent_people_community_add_28_regular.svg"
                      size={14}
                      color={this.props.targetingStore.highlightColor}
                      style={{
                        float: "left",
                      }}
                    />
                    <div
                      style={{
                        color: "#333333",
                        float: "left",
                        marginLeft: 7,
                      }}
                    >
                      {this.localizationService.strings.TargetingAdmin_AddADGroup}
                    </div>
                  </div>
                  <div
                    className={styles.IA_commandBarItem}
                    onClick={() => {
                      this.props.targetingStore.addNewTopLevelGroupHeader = true;
                      this.props.targetingStore.groupHeaderToEdit = {
                        title: "",
                        children: []
                      };
                    }}
                  >
                    <IAIcon
                      url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Text%20Add/SVG/ic_fluent_text_add_20_regular.svg"
                      size={14}
                      color={this.props.targetingStore.highlightColor}
                      style={{
                        float: "left",
                      }}
                    />
                    <div
                      style={{
                        color: "#333333",
                        float: "left",
                        marginLeft: 7,
                      }}
                    >
                      {this.localizationService.strings.TargetingAdmin_AddGroupHeader}
                    </div>
                  </div>
                </ul>
              }
            </div>
            {allInUse ?
              saveButton
              :
              <IATooltip
                content="Tooltip"
                type={"bubble"}
                styles={{
                  width: 200,
                  color: "#999999",
                  textAlign: "center",
                  marginBottom: "5px",
                  borderRadius: 10
                }}
                componentContent={
                  <div>
                    {this.localizationService.strings.TargetingAdmin_SavingDisabledBecauseOfMissingGroups}
                  </div>
                }>
                {saveButton}
              </IATooltip>
            }
            <IAButton
              label={this.localizationService.strings.Admin_Cancel}
              iconColor={"#ffffff"}
              textColor={"#ffffff"}
              borderRadius={5}
              disbaled={JSON.stringify(this.props.targetingStore.structuredGroupsBeforeChanges) === JSON.stringify(this.props.targetingStore.structuredGroups) || this.state.loadingGroupStructure}
              showSpinner={this.state.saving}
              buttonColor={"gray"}
              onClick={() => {
                this.props.targetingStore.structuredGroups = JSON.parse(JSON.stringify(this.props.targetingStore.structuredGroupsBeforeChanges));
              }}
              style={{
                marginLeft: 10,
              }}
            />
            {this.state.loadingGroupStructure ?
              <div className={styles.IA_groupItems}>
                <IASpinner />
              </div>
              :
              <div
                className={styles.IA_groupItems}
                style={{
                  opacity: structureNotSetUp ? 0.5 : 1,
                  userSelect: structureNotSetUp ? "none" : "unset"
                }}
              >
                <StructureViewer
                  style={{
                    clear: "both"
                  }}
                  structure={structure}
                  highlightColor={this.props.targetingStore.highlightColor}
                  elementActions={structureNotSetUp ? [] : [
                    {
                      actionTitle: () => this.localizationService.strings.Card_CommandBar_Edit,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Edit/SVG/ic_fluent_edit_48_regular.svg",
                      action: (element) => this.props.targetingStore.groupHeaderToEdit = element.originalElement,
                      isEnabled: (props) => props.element.type === "header"
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_AddADGroup,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/People%20Community%20Add/SVG/ic_fluent_people_community_add_28_regular.svg",
                      action: (element) => {
                        element.originalElement.children = element.originalElement.children ? element.originalElement.children : [];
                        this.props.targetingStore.groupHeaderToAddTo = element.originalElement;
                      },
                      isEnabled: () => true
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_AddGroupHeader,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Text%20Add/SVG/ic_fluent_text_add_20_regular.svg",
                      action: (element) => {
                        element.originalElement.children = element.originalElement.children ? element.originalElement.children : [];
                        this.props.targetingStore.groupHeaderToAddNewHeaderTo = element.originalElement;
                        this.props.targetingStore.groupHeaderToEdit = {
                          title: "",
                          children: []
                        }
                      },
                      isEnabled: () => true
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_MoveUp,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Arrow%20Up/SVG/ic_fluent_arrow_up_48_regular.svg",
                      action: (element) => {
                        this.props.targetingStore.structuredGroups = this.moveItem(element.originalElement, this.props.targetingStore.structuredGroups, "up");
                        this.forceUpdate();
                      },
                      isEnabled: (props) => props.index !== 0
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_MoveDown,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Arrow%20Down/SVG/ic_fluent_arrow_down_48_regular.svg",
                      action: (element) => {
                        this.props.targetingStore.structuredGroups = this.moveItem(element.originalElement, this.props.targetingStore.structuredGroups, "down");
                        this.forceUpdate();
                      },
                      isEnabled: (props) => props.index !== props.parentChildrenCount - 1
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_MoveOut,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Arrow%20Enter%20Up/SVG/ic_fluent_arrow_enter_up_24_regular.svg",
                      action: (element) => {
                        this.props.targetingStore.structuredGroups = this.moveItemOut(element.originalElement, this.props.targetingStore.structuredGroups);
                        this.forceUpdate();
                      },
                      isEnabled: (props) => !props.isTopLevel
                    },
                    {
                      actionTitle: (props) => props?.isHighlighted ? this.localizationService.strings.TargetingAdmin_RemoveHighlight : this.localizationService.strings.TargetingAdmin_Highlight,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Highlight/SVG/ic_fluent_highlight_24_regular.svg",
                      action: (element) => {
                        this.props.targetingStore.structuredGroups = this.setHighlightForItemInGroups(element.originalElement, this.props.targetingStore.structuredGroups);
                        this.forceUpdate();
                      },
                      isEnabled: () => true
                    },
                    {
                      actionTitle: () => this.localizationService.strings.TargetingAdmin_Remove,
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Eraser/SVG/ic_fluent_eraser_24_regular.svg",
                      action: (element) => {
                        this.props.targetingStore.structuredGroups = this.deleteItemInGroups(element.originalElement, this.props.targetingStore.structuredGroups);
                        this.forceUpdate();
                      },
                      isEnabled: () => true
                    }
                  ]}
                />
              </div>
            }
            <GroupHeaderEditPanel
              title={this.props.targetingStore.groupHeaderToEdit?.title}
              highlightColor={this.props.targetingStore.highlightColor}
              onClose={() => {
                this.props.targetingStore.groupHeaderToEdit = undefined;
                this.props.targetingStore.addNewTopLevelGroupHeader = false;
              }}
              onSave={(newTitle: string) => {
                if (this.props.targetingStore.groupHeaderToAddNewHeaderTo) {
                  this.props.targetingStore.groupHeaderToAddNewHeaderTo.children?.push({
                    title: newTitle,
                    children: []
                  });
                } else if (this.props.targetingStore.addNewTopLevelGroupHeader) {
                  this.props.targetingStore.structuredGroups?.push({
                    title: newTitle,
                    children: []
                  });
                } else {
                  this.props.targetingStore.structuredGroups = this.updateGroupHeaderTitle(newTitle, this.props.targetingStore.structuredGroups);
                }
                this.props.targetingStore.groupHeaderToEdit = undefined;
                this.props.targetingStore.groupHeaderToAddNewHeaderTo = undefined;
                this.props.targetingStore.addNewTopLevelGroupHeader = false;
              }}
            />
            <ADGroupPanel
              open={this.props.targetingStore.groupHeaderToAddTo != undefined || this.props.targetingStore.addNewTopLevelADGroup}
              availableGroups={this.getUnselectedADGroups()}
              highlightColor={this.props.targetingStore.highlightColor}
              onClose={() => {
                this.props.targetingStore.groupHeaderToAddTo = undefined;
                this.props.targetingStore.addNewTopLevelADGroup = false;
              }}
              onSave={(groupIds: number[]) => {
                groupIds?.forEach((groupId: number) => {
                  if (this.props.targetingStore.addNewTopLevelADGroup) {
                    this.props.targetingStore.structuredGroups.push(
                      {
                        id: groupId,
                        children: []
                      }
                    );
                  } else {
                    console.log(this.props.targetingStore.groupHeaderToAddTo);
                    this.props.targetingStore.groupHeaderToAddTo.children = this.props.targetingStore.groupHeaderToAddTo.children ? this.props.targetingStore.groupHeaderToAddTo.children : [];
                    this.props.targetingStore.groupHeaderToAddTo.children.push(
                      {
                        id: groupId,
                        children: []
                      }
                    );
                  }
                });
                setTimeout(() => {
                  this.props.targetingStore.groupHeaderToAddTo = undefined;
                  this.props.targetingStore.addNewTopLevelADGroup = false;
                }, 0);
              }}
            />
          </div>
        }
        {/* ADGroups */}
        {this.state.selectedTab === "ADGroups" &&
          <div style={{
            float: 'left',
            clear: "both",
            width: "100%",
            paddingBottom: 50,
            boxSizing: "border-box"
          }}>
            <IACommandBar
              highlightColor={this.props.targetingStore.highlightColor}
              showBar={true}
              leftSideContent={
                <div style={{ height: 40 }}>
                  <IAButton
                    label={this.localizationService.strings.Messages_Add}
                    disbaled={this.props.targetingStore.loading}
                    buttonColor={this.props.targetingStore.highlightColor}
                    borderRadius={5}
                    icon="Add"
                    style={{ float: "left" }}
                    dataAutomationId="messages-add-new"
                    onClick={() => {
                      this.props.targetingStore.userGroupToEdit = {
                        title: undefined,
                        values: undefined,
                        id: undefined
                      };
                      this.props.targetingStore.originalUserGroupToEdit = Object.assign({}, this.props.targetingStore.userGroupToEdit);
                      this.props.targetingStore.creatingNew = true;
                    }}
                  />
                </div>
              }
              rightSideContent={<div></div>}
              style={{
                marginBottom: 20
              }}
            />
            {this.props.targetingStore.groupedList && !this.props.targetingStore.loading &&
              <IAGroupList
                highlightColor={this.props.targetingStore.highlightColor}
                groupsItem={this.props.targetingStore.groupedList}
                getUpdates={(items, selectedItem) => {
                  console.log(items);
                  const selectedItemUG = this.props.targetingStore.groupsList.filter(x => x.id === selectedItem.id)[0];
                  this.props.targetingStore.userGroupToEdit =
                  {
                    id: selectedItemUG.id,
                    title: selectedItemUG.title,
                    values: selectedItemUG.values
                  };
                  this.props.targetingStore.creatingNew = false;
                }}
              />
            }
            {this.props.targetingStore.loading &&
              <div className={styles.IA_groupItems}>
                <IASpinner />
              </div>
            }
            <EditPanel
              reloadGroups={() => {
                document.body.style.height = "inherit";
                document.body.style.overflowY = "auto";
                this.props.reloadGroups()
              }} />
            {!this.props.targetingStore.loading && this.props.targetingStore.groupsList !== undefined &&
              this.props.targetingStore.groupsList[0] !== undefined && this.props.targetingStore.groupsList[0][0] === undefined &&
              this.props.targetingStore.groupsList.length === 0 &&
              <div className={styles.IA_listViewDivider}>{this.localizationService.strings.TargetingAdmin_UserGroup_NoGroups}</div>
            }

            <div id={`deletePopupId_${this.deletePopupId}`}>
              {this.props.targetingStore.userGroupToDelete !== undefined &&
                <IAPopup
                  headline={this.localizationService.strings.DeletePopup_Headline}
                  content={
                    <div>
                      <div
                        style={{
                          float: "left"
                        }}
                      >
                        {this.localizationService.strings.DeletePopup_Message}
                      </div>
                      <div
                        style={{
                          clear: "both",
                          float: "left"
                        }}
                      >
                        <IAButton
                          label={this.localizationService.strings.DeletePopup_Yes}
                          buttonColor={this.props.targetingStore.highlightColor}
                          borderRadius={5}
                          showSpinner={false}
                          onClick={() => {
                            TargetingService.deleteGroup(this.props.targetingStore.environment, this.props.targetingStore.tenant, this.props.targetingStore.token, this.props.targetingStore.userGroupToDelete.id).then(() => {
                              this.props.targetingStore.userGroupToEdit = undefined;
                              this.props.targetingStore.userGroupToDelete = undefined;
                              document.body.style.height = "inherit";
                              document.body.style.overflowY = "auto";
                              this.props.reloadGroups();
                            });
                          }}
                          style={{
                            marginTop: 20,
                            float: "left"
                          }}
                        />
                        <IAButton
                          label={this.localizationService.strings.DeletePopup_No}
                          buttonColor={"#999999"}
                          borderRadius={5}
                          showSpinner={false}
                          onClick={() => {
                            this.props.targetingStore.userGroupToDelete = undefined;
                          }}
                          style={{
                            marginTop: 20,
                            marginLeft: 10,
                            float: "left"
                          }}
                        />
                      </div>
                    </div>
                  }
                  close={() => {
                    this.props.targetingStore.userGroupToDelete = undefined;
                  }}
                />
              }
            </div>

            <div id={`cancelPopupId_${this.cancelPopupId}`}>
              {this.props.targetingStore.showCancelEditPopup &&
                <IAPopup
                  headline={this.localizationService.strings.CancelPopup_Headline}
                  content={
                    <div>
                      <IAButton
                        label={this.localizationService.strings.CancelPopup_Yes}
                        buttonColor={this.props.targetingStore.highlightColor}
                        borderRadius={5}
                        showSpinner={false}
                        onClick={() => {
                          this.props.targetingStore.userGroupToEdit = undefined;
                          this.props.targetingStore.originalUserGroupToEdit = undefined;
                          this.props.targetingStore.creatingNew = false;
                          this.props.targetingStore.showCancelEditPopup = false;
                          document.body.style.height = "inherit";
                          document.body.style.overflowY = "auto";
                        }}
                        style={{
                          marginTop: 20,
                          float: "left"
                        }}
                      />
                      <IAButton
                        label={this.localizationService.strings.CancelPopup_No}
                        buttonColor={"#999999"}
                        borderRadius={5}
                        showSpinner={false}
                        onClick={() => {
                          this.props.targetingStore.showCancelEditPopup = false;
                        }}
                        style={{
                          marginTop: 20,
                          marginLeft: 10,
                          float: "left"
                        }}
                      />
                    </div>
                  }
                  close={() => {
                    this.props.targetingStore.showCancelEditPopup = false;
                  }}
                />
              }
            </div>
          </div>
        }
      </div >
    );
  }
}